import React, { useEffect, useState } from 'react';
import { configApi, IdentityProviderStatus } from '../../../api/config';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../../hooks/useToast';

const NAMES = { interswitch: 'Interswitch', dojah: 'Dojah' } as const;
type Provider = keyof typeof NAMES;

/**
 * Which provider answers BVN and NIN first. The other is always asked behind
 * it, so this is for routing around an outage or a cost, not for turning one
 * off. Liveness and the selfie match stay on Dojah whichever is chosen.
 */
export const IdentityProvider: React.FC = () => {
  const { can } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState<IdentityProviderStatus | null>(null);
  const [pending, setPending] = useState<Provider | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    configApi
      .getIdentityProvider()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  if (!status) return null;
  const other: Provider = status.primary === 'dojah' ? 'interswitch' : 'dojah';

  const save = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      setStatus(await configApi.updateIdentityProvider(pending));
      toast.success(`${NAMES[pending]} now answers BVN and NIN first.`);
      setPending(null);
    } catch (err) {
      toast.error(err, 'Failed to update configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding={0}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
          BVN and NIN Verification
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          The primary answers first. If it is down or cannot say, the other is asked automatically. Liveness and the selfie match stay on Dojah either way.
        </p>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(min(100%, 220px), 1fr) auto auto',
          gap: 16,
          padding: '16px 20px',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--neutral-800)' }}>Primary provider</div>
          <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 2 }}>
            {status.interswitchConfigured
              ? `${NAMES[other]} is the fallback.`
              : 'Interswitch has no credentials on this server, so only Dojah is asked.'}
          </div>
        </div>
        <Badge variant="info">{NAMES[status.primary]}</Badge>
        {can('config.system') ? (
          <Button size="sm" variant="secondary" onClick={() => setPending(other)}>
            Use {NAMES[other]}
          </Button>
        ) : null}
      </div>
      <ConfirmDialog
        isOpen={pending !== null}
        title="Change the primary provider?"
        message={
          pending
            ? `${NAMES[pending]} will answer BVN and NIN first, from the next lookup, with ${NAMES[pending === 'dojah' ? 'interswitch' : 'dojah']} behind it.`
            : ''
        }
        confirmLabel="Update"
        variant="primary"
        loading={saving}
        onConfirm={() => void save()}
        onCancel={() => setPending(null)}
      />
    </Card>
  );
};
